import React, { useState, useEffect } from 'react';
import { Search, Lock, X, CheckCircle2, Hash } from 'lucide-react';
import slackIcon from '@/assets/slack-icon.png';

interface Channel {
  id: string;
  name: string;
  is_private: boolean;
}

interface ChannelSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (channelId: string) => void;
  channels: Channel[];
  isLoading: boolean;
  selectedChannelId?: string;
}

const ChannelSelector: React.FC<ChannelSelectorProps> = ({
  isOpen,
  onClose,
  onSelect,
  channels,
  isLoading,
  selectedChannelId
}) => {
  const [tempSelectedId, setTempSelectedId] = useState<string | undefined>(selectedChannelId);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSelect = (channelId: string) => {
    setTempSelectedId(channelId);
  };

  const handleConfirm = () => {
    if (tempSelectedId) {
      onSelect(tempSelectedId);
      onClose();
    }
  };

  const filteredChannels = channels.filter(channel =>
    channel.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div 
        className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      <div className="relative w-[626px] bg-white shadow-lg rounded-[16px] transform transition-transform duration-300 ease-out">
        {/* Header */}
        <div className="px-5 py-3 border-b border-[#EEEEEE]">
          <div className="flex items-center gap-2">
            <img src={slackIcon} alt="Slack" className="w-4 h-4" />
            <h2 className="text-sm font-normal text-[#222222] ">Select slack channel</h2>
          </div>
        </div>

        {/* Search */}
        <div className="w-full px-[18px] py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#7A7A7A] w-[14px] h-[14px]" />
            <input
              type="text"
              placeholder="Search channels..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-1 bg-white border border-[#EEEEEE] rounded-lg
                text-sm focus:outline-none focus:ring-1 focus:ring-[#EEEEEE] 
                transition-all placeholder:text-[#7A7A7A]"
            />
          </div>
        </div>

        {/* Channel Heading - Outside scroll area */}
        <div className="px-[18px] text-left">
          <h3 className="text-xs font-normal text-[#222222] mb-2 ">Channel</h3>
        </div>

        {/* Channel List - Scrollable */}
        <div className="px-[18px] mt-2 mb-[32px]">
          <div className="overflow-y-auto scrollbar-hide" style={{ height: '320px' }}>
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-[200px]">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#EEEEEE] border-t-[#222222]" />
              </div>
            ) : filteredChannels.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-[200px] text-[#7A7A7A]">
                <p>No channels found</p>
              </div>
            ) : (
              <div className="space-y-1">
                {filteredChannels.map((channel) => (
                  <button
                    key={channel.id}
                    onClick={() => handleSelect(channel.id)}
                    className={`w-full text-left px-3 py-2 flex items-center gap-2 rounded-lg
                      transition-colors hover:bg-[#FAFAFA]
                      ${tempSelectedId === channel.id ? 'bg-[#FAFAFA]' : ''}`}
                  >
                    <Hash className="w-4 h-4 text-[#7A7A7A]" />
                    <span className="text-sm text-[#222222]">{channel.name}</span>
                    {channel.is_private && (
                      <Lock className="w-3 h-3 text-[#7A7A7A] ml-1" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-[#EEEEEE] flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-normal text-[#222222] hover:bg-[#FAFAFA] rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!tempSelectedId}
            className={`px-4 py-2 text-sm font-normal rounded-lg
              ${!tempSelectedId 
                ? 'bg-[#EEEEEE] text-[#7A7A7A] cursor-not-allowed' 
                : 'bg-[#42E56F] text-[#222222] hover:bg-[#22c55e]'}`}
          >
            Confirm selection
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChannelSelector;